import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import {
  AppWindow,
  BarChart3,
  Bug,
  DollarSign,
  FolderOpen,
  HelpCircle,
  Lock,
  Minus,
  Play,
  Search,
  Settings,
  Square,
  Store,
  X,
} from 'lucide-react';
import { WindowData } from '../hooks/useOS';
import { WindowContent } from './WindowContent';
import { cn } from '../lib/utils';
import { ContextMenu, useContextMenu, ContextMenuItem } from './ui/context-menu-custom';
import { reportActivity } from '../lib/activity';
import { PROGRAM_MAP } from '../utils/programs';
import { makeOverrideIcon } from './AppIcon';
import {
  CONTROL_PANEL_WINDOW_ICONS,
  ControlPanelUsersIcon,
} from './apps/controlPanelIcons';

const SearchTitlebarIcon = makeOverrideIcon('search', Search);
const RunTitlebarIcon = makeOverrideIcon('run-menu', Play);
const HelpTitlebarIcon = makeOverrideIcon('help-menu', HelpCircle);
const FileExplorerTitlebarIcon = makeOverrideIcon('file-explorer', FolderOpen);
const ControlPanelTitlebarIcon = makeOverrideIcon('control-panel', Settings);
const MapleSlideTitlebarIcon = makeOverrideIcon('maple-slide', BarChart3);
const MapleMoneyTitlebarIcon = makeOverrideIcon('maple-money', DollarSign);
const MapleStatsTitlebarIcon = makeOverrideIcon('maple-stats', BarChart3);
const BugReportTitlebarIcon = makeOverrideIcon('bug-report', Bug);
const EncryptionTitlebarIcon = makeOverrideIcon('encryption-settings', Lock);
const AppStoreTitlebarIcon = makeOverrideIcon('maple-market', Store);

type TitlebarIconProps = {
  size?: number | string;
  className?: string;
};

const WINDOW_TITLEBAR_ICON_MAP: Record<string, React.ComponentType<TitlebarIconProps>> = {
  'search': SearchTitlebarIcon,
  'run': RunTitlebarIcon,
  'help': HelpTitlebarIcon,
  'supabase-file-explorer': FileExplorerTitlebarIcon,
  'app-store': AppStoreTitlebarIcon,
  'maple-slide': MapleSlideTitlebarIcon,
  'maple-money': MapleMoneyTitlebarIcon,
  'maple-stats': MapleStatsTitlebarIcon,
  'editor-settings': ControlPanelTitlebarIcon,
  'context-demo': AppWindow,
  'bug-report': BugReportTitlebarIcon,
  'protection-history': ControlPanelTitlebarIcon,
  'encryption-settings': EncryptionTitlebarIcon,
  'ai-isolation': ControlPanelTitlebarIcon,
  'canxp-module': AppWindow,
  'module-host': AppWindow,
  'web-app-runner': AppWindow,
  'user-settings': ControlPanelUsersIcon,
  ...CONTROL_PANEL_WINDOW_ICONS,
};

function getWindowTitlebarIcon(appId: string) {
  return WINDOW_TITLEBAR_ICON_MAP[appId] || PROGRAM_MAP[appId]?.icon || AppWindow;
}

interface OSWindowProps {
  window: WindowData;
  isActive: boolean;
  fullScreenShellOverlayActive: boolean;
  startMenuOpen: boolean;
  contextMenuVisible: boolean;
  onClose: (windowId: string) => void;
  onMinimize: (windowId: string) => void;
  onMaximize: (windowId: string) => void;
  onBoundsChange: (windowId: string, bounds: { x: number; y: number; width: number; height: number }) => void;
  onFocus: (windowId: string) => void;
  onOpenWindow?: (appId: string, title: string, filePath?: string, content?: string) => void;
  onBackgroundChange?: (background: string) => void;
  onShowSplash?: (show: boolean) => void;
}

export const OSWindow: React.FC<OSWindowProps> = ({
  window,
  isActive,
  fullScreenShellOverlayActive,
  startMenuOpen,
  contextMenuVisible,
  onClose,
  onMinimize,
  onMaximize,
  onBoundsChange,
  onFocus,
  onOpenWindow,
  onBackgroundChange,
  onShowSplash
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<string>('');
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: window.x, y: window.y });
  const [size, setSize] = useState({ width: window.width, height: window.height });
  const [lastTapTime, setLastTapTime] = useState(0);
  const [tapCount, setTapCount] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const windowRef = useRef<HTMLDivElement>(null);
  const { contextMenu, showContextMenu, hideContextMenu } = useContextMenu();
  const TitlebarIcon = getWindowTitlebarIcon(window.appId);

  // Check if running in Electron (for title bar offset)
  const isElectron = typeof globalThis.window !== 'undefined' && (globalThis.window as any).electronAPI?.isElectron;
  const TASKBAR_HEIGHT = 48; // 48px taskbar at bottom

  const getAvailableBounds = React.useCallback(() => {
    const parentRect = windowRef.current?.parentElement?.getBoundingClientRect();
    const topOffset = parentRect?.top ?? 0;

    return {
      width: parentRect?.width ?? globalThis.window.innerWidth,
      height: globalThis.window.innerHeight - TASKBAR_HEIGHT - topOffset,
      topOffset,
    };
  }, [TASKBAR_HEIGHT]);

  // Force re-render on window resize to update maximized dimensions
  const [, setTick] = useState(0);
  React.useEffect(() => {
    const handleResize = () => setTick(t => t + 1);
    globalThis.window.addEventListener('resize', handleResize);
    return () => globalThis.window.removeEventListener('resize', handleResize);
  }, []);

  // Poll for fullscreen state changes in Electron
  React.useEffect(() => {
    if (isElectron) {
      // Check initial state
      (globalThis.window as any).electronAPI.windowIsFullScreen().then(setIsFullScreen);

      // Poll for changes (F11 can be pressed)
      const interval = setInterval(() => {
        (globalThis.window as any).electronAPI.windowIsFullScreen().then(setIsFullScreen);
      }, 500);

      return () => clearInterval(interval);
    }
  }, [isElectron]);

  // NEW: live refs for transform/size that we update imperatively (no React re-render during drag/resize)
  const livePositionRef = React.useRef<{ x: number; y: number }>({ x: window.x, y: window.y });
  const liveSizeRef = React.useRef<{ width: number; height: number }>({ width: window.width, height: window.height });

  // NEW: snap values to device pixels to avoid fractional transforms (Chromium tearing)
  const snapToDevicePixels = (v: number) => {
    const dpr = globalThis.window?.devicePixelRatio ?? 1;
    return Math.round(v * dpr) / dpr;
  };

  const applyTransform = (x: number, y: number) => {
    const sx = snapToDevicePixels(x);
    const sy = snapToDevicePixels(y);
    livePositionRef.current = { x: sx, y: sy };
    const el = windowRef.current;
    if (el) {
      el.style.transform = `translate3d(${sx}px, ${sy}px, 0)`;
    }
  };

  const applySize = (width: number, height: number) => {
    const sw = snapToDevicePixels(width);
    const sh = snapToDevicePixels(height);
    liveSizeRef.current = { width: sw, height: sh };
    const el = windowRef.current;
    if (el) {
      el.style.width = `${sw}px`;
      el.style.height = `${sh}px`;
    }
  };

  const getOverlayRect = React.useCallback((selector: string) => {
    const node = document.querySelector(selector) as HTMLElement | null;
    if (!node) {
      return null;
    }

    let target = node;
    const rect = node.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      const firstChild = node.firstElementChild as HTMLElement | null;
      if (firstChild) {
        target = firstChild;
      }
    }

    const targetRect = target.getBoundingClientRect();
    if (targetRect.width === 0 || targetRect.height === 0) {
      return null;
    }

    return targetRect;
  }, []);

  const doesRectOverlap = React.useCallback((a: DOMRect, b: DOMRect) => {
    return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
  }, []);

  const isOccludedByPartialShellOverlay = React.useCallback(() => {
    if (window.appId !== 'web-browser') {
      return false;
    }

    const windowRect = windowRef.current?.getBoundingClientRect();
    if (!windowRect || windowRect.width <= 0 || windowRect.height <= 0) {
      return false;
    }

    if (startMenuOpen) {
      const startMenuRect = getOverlayRect('[data-start-menu]');
      if (startMenuRect && doesRectOverlap(windowRect, startMenuRect)) {
        return true;
      }
    }

    if (contextMenuVisible) {
      const contextMenuRect = getOverlayRect('[data-context-menu]');
      if (contextMenuRect && doesRectOverlap(windowRect, contextMenuRect)) {
        return true;
      }
    }

    return false;
  }, [contextMenuVisible, doesRectOverlap, getOverlayRect, startMenuOpen, window.appId]);

  const emitBrowserViewportChange = React.useCallback(() => {
    if (window.appId !== 'web-browser') {
      return;
    }

    const blockedByOverlay = fullScreenShellOverlayActive || isOccludedByPartialShellOverlay();

    globalThis.window.dispatchEvent(
      new CustomEvent('mapleos:window-viewport-change', {
        detail: {
          windowId: window.id,
          visible: !window.isMinimized && isActive && !blockedByOverlay,
        },
      })
    );
  }, [fullScreenShellOverlayActive, isActive, isOccludedByPartialShellOverlay, window.appId, window.id, window.isMinimized]);

  React.useEffect(() => {
    if (isDragging || isResizing) {
      return;
    }

    const nextPosition = {
      x: snapToDevicePixels(window.x),
      y: snapToDevicePixels(window.y),
    };
    const nextSize = {
      width: snapToDevicePixels(window.width),
      height: snapToDevicePixels(window.height),
    };

    setPosition(nextPosition);
    setSize(nextSize);
    livePositionRef.current = nextPosition;
    liveSizeRef.current = nextSize;
  }, [
    window.id,
    window.x,
    window.y,
    window.width,
    window.height,
    window.isMaximized,
    isDragging,
    isResizing,
  ]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget || (e.target as HTMLElement).closest('.window-header')) {
      if (!window.isMaximized) {
        const currentRect = windowRef.current?.getBoundingClientRect();
        const currentX = currentRect?.left ?? position.x;
        const currentY = currentRect?.top ?? position.y;

        setIsDragging(true);
        setDragOffset({
          x: e.clientX - currentX,
          y: e.clientY - currentY
        });
        // Ensure DOM has the current transform before React style changes
        applyTransform(currentX, currentY);
        applySize(size.width, size.height);
      }
      reportActivity(window.id, window.appId, 5);
      onFocus(window.id);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.target === e.currentTarget || (e.target as HTMLElement).closest('.window-header')) {
      const currentTime = Date.now();
      const timeSinceLastTap = currentTime - lastTapTime;
      
      // Check for double tap (within 300ms) - only if maximize is allowed
      if (timeSinceLastTap < 300 && timeSinceLastTap > 50 && !window.fixedSize && window.canMaximize !== false) {
        // Double tap detected - maximize/restore window
        e.preventDefault();
        onMaximize(window.id);
        setTapCount(0);
        setLastTapTime(0);
        return;
      }
      
      // Set up for potential drag operation immediately
      setLastTapTime(currentTime);
      setTapCount(1);
      
      if (!window.isMaximized && e.touches.length === 1) {
        const touch = e.touches[0];
        const currentRect = windowRef.current?.getBoundingClientRect();
        const currentX = currentRect?.left ?? position.x;
        const currentY = currentRect?.top ?? position.y;

        setDragOffset({
          x: touch.clientX - currentX,
          y: touch.clientY - currentY
        });
        // Start dragging immediately - this will be cancelled if double tap is detected
        setIsDragging(true);
        applyTransform(currentX, currentY);
        applySize(size.width, size.height);
      }
      
      onFocus(window.id);
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging && !window.isMaximized) {
      const { width: availableWidth, height: availableHeight, topOffset } = getAvailableBounds();
      const w = liveSizeRef.current.width;
      const h = liveSizeRef.current.height;
      const minY = topOffset;
      const maxY = topOffset + availableHeight - h;
      const newX = Math.max(0, Math.min(e.clientX - dragOffset.x, availableWidth - w));
      const newY = Math.max(minY, Math.min(e.clientY - dragOffset.y, maxY));

      applyTransform(snapToDevicePixels(newX), snapToDevicePixels(newY));
      reportActivity(window.id, window.appId, 10);
      emitBrowserViewportChange();
    } else if (isResizing && !window.isMaximized) {
      const rect = windowRef.current?.getBoundingClientRect();
      if (!rect) return;

      const { width: availableWidth, height: availableHeight, topOffset } = getAvailableBounds();
      const maxBottom = topOffset + availableHeight;
      let newWidth = liveSizeRef.current.width;
      let newHeight = liveSizeRef.current.height;
      let newX = livePositionRef.current.x;
      let newY = livePositionRef.current.y;

      // Handle different resize directions with proper constraints
      if (resizeDirection.includes('right')) {
        const potentialWidth = Math.max(200, e.clientX - newX);
        newWidth = Math.min(potentialWidth, availableWidth - newX);
      }
      if (resizeDirection.includes('left')) {
        const deltaX = e.clientX - newX;
        const potentialWidth = liveSizeRef.current.width - deltaX;
        if (potentialWidth >= 200) {
          newWidth = potentialWidth;
          newX = Math.max(0, e.clientX);
        }
      }
      if (resizeDirection.includes('bottom')) {
        const potentialHeight = Math.max(150, e.clientY - newY);
        newHeight = Math.min(potentialHeight, maxBottom - newY);
      }
      if (resizeDirection.includes('top')) {
        const deltaY = e.clientY - newY;
        const potentialHeight = liveSizeRef.current.height - deltaY;
        if (potentialHeight >= 150) {
          newHeight = potentialHeight;
          newY = Math.max(topOffset, e.clientY);
        }
      }

      // Final boundary checks
      if (newX + newWidth > availableWidth) {
        newWidth = availableWidth - newX;
      }
      if (newY + newHeight > maxBottom) {
        newHeight = maxBottom - newY;
      }

      // Only update if we have valid dimensions
      if (newWidth >= 200 && newHeight >= 150) {
        applySize(snapToDevicePixels(newWidth), snapToDevicePixels(newHeight));
        applyTransform(snapToDevicePixels(newX), snapToDevicePixels(newY));
        // NEW: report interactive activity (resizing)
        reportActivity(window.id, window.appId, 12);
        emitBrowserViewportChange();
      }
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (isDragging && !window.isMaximized && e.touches.length === 1) {
      e.preventDefault();
      const { width: availableWidth, height: availableHeight, topOffset } = getAvailableBounds();
      const touch = e.touches[0];
      const w = liveSizeRef.current.width;
      const h = liveSizeRef.current.height;
      const minY = topOffset;
      const maxY = topOffset + availableHeight - h;
      const newX = Math.max(0, Math.min(touch.clientX - dragOffset.x, availableWidth - w));
      const newY = Math.max(minY, Math.min(touch.clientY - dragOffset.y, maxY));

      applyTransform(snapToDevicePixels(newX), snapToDevicePixels(newY));
      // NEW: report interactive activity (touch-drag)
      reportActivity(window.id, window.appId, 10);
      emitBrowserViewportChange();
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeDirection('');
    // Commit final values back to React state once (avoids re-render storms in Chrome/Edge)
    setPosition({ x: livePositionRef.current.x, y: livePositionRef.current.y });
    setSize({ width: liveSizeRef.current.width, height: liveSizeRef.current.height });
    onBoundsChange(window.id, {
      x: livePositionRef.current.x,
      y: livePositionRef.current.y,
      width: liveSizeRef.current.width,
      height: liveSizeRef.current.height,
    });
    emitBrowserViewportChange();
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeDirection('');
    setPosition({ x: livePositionRef.current.x, y: livePositionRef.current.y });
    setSize({ width: liveSizeRef.current.width, height: liveSizeRef.current.height });
    onBoundsChange(window.id, {
      x: livePositionRef.current.x,
      y: livePositionRef.current.y,
      width: liveSizeRef.current.width,
      height: liveSizeRef.current.height,
    });
    emitBrowserViewportChange();
  };

  const handleResizeStart = (direction: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    setResizeDirection(direction);
    // Seed DOM with current values so React doesn't overwrite during the first frame
    const currentRect = windowRef.current?.getBoundingClientRect();
    applyTransform(currentRect?.left ?? position.x, currentRect?.top ?? position.y);
    applySize(size.width, size.height);
  };

  React.useEffect(() => {
    if (window.appId === 'terminal') {
      // After mount, request terminal to focus its input (let other UI settle first)
      const t = globalThis.setTimeout(() => {
        const focusEvent = new CustomEvent('focusTerminalInput', { detail: { windowId: window.id } });
        document.dispatchEvent(focusEvent);
      }, 0);
      return () => globalThis.clearTimeout(t);
    }
  }, [window.appId, window.id]);

  React.useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, isResizing, dragOffset, position, size, resizeDirection]);

  React.useEffect(() => {
    const frame = globalThis.window.requestAnimationFrame(() => {
      emitBrowserViewportChange();
    });

    return () => globalThis.window.cancelAnimationFrame(frame);
  }, [
    contextMenuVisible,
    emitBrowserViewportChange,
    fullScreenShellOverlayActive,
    isActive,
    startMenuOpen,
    window.appId,
    window.height,
    window.isMaximized,
    window.isMinimized,
    window.width,
    window.x,
    window.y,
  ]);

  // Before return: compute snapped values for render style (so React never paints fractional values)

  const { width: availableWidth, height: availableHeight, topOffset } = getAvailableBounds();
  const renderWidth = snapToDevicePixels(
    window.isMaximized
      ? availableWidth
      : (isResizing ? liveSizeRef.current.width : size.width)
  );
  const renderHeight = snapToDevicePixels(
    window.isMaximized
      ? Math.max(0, availableHeight)
      : (isResizing ? liveSizeRef.current.height : size.height)
  );
  const renderX = snapToDevicePixels(
    window.isMaximized
      ? 0
      : ((isDragging || isResizing) ? livePositionRef.current.x : position.x)
  );
  const renderY = snapToDevicePixels(
    window.isMaximized
      ? topOffset
      : Math.max(
          topOffset,
          (isDragging || isResizing) ? livePositionRef.current.y : position.y
        )
  );

  return (
    <div
      ref={windowRef}
      className={cn(
        "fixed shadow-xl flex flex-col overflow-hidden",
        window.appId === 'terminal' ? 'bg-transparent' : 'bg-card',
        isDragging && "select-none"
      )}
      style={{
        display: window.isMinimized ? 'none' : 'flex',
        left: 0,
        top: 0,
        // Use snapped values to avoid subpixel rendering
        width: renderWidth,
        height: renderHeight,
        zIndex: window.zIndex,
        transformStyle: 'flat',
        transform: `translate3d(${renderX}px, ${renderY}px, 0)`,
        backfaceVisibility: 'hidden',
        contain: 'layout paint style',
        isolation: 'isolate',
        // Keep will-change minimal to avoid extra raster work
        willChange: 'transform',
        transition: window.isMaximized && !isDragging && !isResizing
          ? 'transform 300ms ease, width 300ms ease, height 300ms ease'
          : undefined,
        backgroundClip: 'padding-box',
        // Drop shadow entirely while moving/resizing to reduce compositor work in Chromium
        boxShadow: (isDragging || isResizing)
          ? 'none'
          : `inset 0 0 0 1px hsl(var(--border)), 0 16px 32px rgba(0,0,0,0.16)`
      }}
      data-window-id={window.id}
      data-app-id={window.appId}
      data-title={window.title}
      onMouseDown={(e) => {
        const target = e.target as HTMLElement;
        if (target && target.closest('[data-window-interactive="true"]')) {
          return;
        }
        onFocus(window.id);
        if (window.appId === 'terminal') {
          const focusEvent = new CustomEvent('focusTerminalInput', { detail: { windowId: window.id } });
          document.dispatchEvent(focusEvent);
        }
      }}
      onClick={hideContextMenu}
      onContextMenu={(e) => {
        e.stopPropagation();
      }}
    >
      {/* Window Header */}
      <div
        className="window-header bg-gradient-window text-[hsl(var(--window-header-foreground))] px-3 py-2 flex items-center justify-between cursor-move touch-none"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onDoubleClick={() => {
          // Double-click to maximize/restore - only if maximize is allowed
          if (!window.fixedSize && window.canMaximize !== false) {
            onMaximize(window.id);
          }
        }}
        onContextMenu={(e) => {
          if (window.appId === 'terminal') {
            e.preventDefault();
            const contextItems: ContextMenuItem[] = [
              {
                id: 'settings',
                label: 'Settings',
                icon: <Settings size={16} />,
                onClick: () => {
                  // This will be handled in WindowContent
                  const settingsEvent = new CustomEvent('openTerminalSettings', {
                    detail: { windowId: window.id }
                  });
                  document.dispatchEvent(settingsEvent);
                }
              }
            ];
            showContextMenu(e, contextItems);
          }
        }}
      >
        <div className="flex min-w-0 items-center gap-2">
          <TitlebarIcon size={16} className="h-4 w-4 shrink-0 text-current" />
          <span className="font-medium text-sm truncate">{window.title}</span>
        </div>
        
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 bg-[var(--window-control-bg)] hover:bg-[var(--window-control-bg-hover)]"
            onClick={() => onMinimize(window.id)}
          >
            <Minus size={12} strokeWidth={2.5} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-6 w-6 p-0 bg-[var(--window-control-bg)] hover:bg-[var(--window-control-bg-hover)]",
              (window.fixedSize || window.canMaximize === false) && "opacity-50 cursor-not-allowed"
            )}
            onClick={() => !window.fixedSize && window.canMaximize !== false && onMaximize(window.id)}
            disabled={window.fixedSize || window.canMaximize === false}
          >
            <Square size={10} strokeWidth={2.25} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 bg-[var(--window-control-bg)] hover:bg-destructive hover:text-destructive-foreground"
            onClick={() => onClose(window.id)}
          >
            <X size={12} strokeWidth={2.25} />
          </Button>
        </div>
      </div>

      {/* Window Content */}
      <div
        className={cn(
          "flex-1 min-h-0",
          // MapleWord keeps overflow-visible
          window.appId === 'maple-word'
            ? "overflow-visible"
            // Site-embedded apps (e.g., Chat/Projects via canxp-module, Module Host, and standalone web apps) manage inner scrolling
            : (window.appId === 'canxp-module' || window.appId === 'module-host' || window.appId === 'web-app-runner')
              ? "overflow-hidden"
              : "overflow-auto"
        )}
        style={{
          // Keep this layer promoted and stable to avoid rasterization tears
          contain: 'layout paint style',
          transform: 'translateZ(0)',
          transformStyle: 'flat',
          backfaceVisibility: 'hidden',
          willChange: 'transform',
          // Keep the content clipped to its padding box to avoid edge artifacts while moving
          backgroundClip: 'padding-box'
        }}
      >
        <WindowContent
          appId={window.appId}
          windowId={window.id}
          filePath={window.filePath}
          fileContent={window.fileContent}
          onOpenWindow={onOpenWindow}
          onBackgroundChange={onBackgroundChange}
          onClose={() => onClose(window.id)}
          onShowSplash={onShowSplash}
        />
      </div>

      {/* Resize Handles - only show when not maximized and not fixed size */}
      {!window.isMaximized && !window.fixedSize && (
        <>
          <div className="absolute top-0 left-0 w-2 h-2 cursor-nw-resize z-10" onMouseDown={handleResizeStart('top-left')} />
          <div className="absolute top-0 right-0 w-2 h-2 cursor-ne-resize z-10" onMouseDown={handleResizeStart('top-right')} />
          <div className="absolute bottom-0 left-0 w-2 h-2 cursor-sw-resize z-10" onMouseDown={handleResizeStart('bottom-left')} />
          <div className="absolute bottom-0 right-0 w-2 h-2 cursor-se-resize z-10" onMouseDown={handleResizeStart('bottom-right')} />
          <div className="absolute top-0 left-2 right-2 h-1 cursor-n-resize z-10" onMouseDown={handleResizeStart('top')} />
          <div className="absolute bottom-0 left-2 right-2 h-1 cursor-s-resize z-10" onMouseDown={handleResizeStart('bottom')} />
          <div className="absolute left-0 top-2 bottom-2 w-1 cursor-w-resize z-10" onMouseDown={handleResizeStart('left')} />
          <div className="absolute right-0 top-2 bottom-2 w-1 cursor-e-resize z-10" onMouseDown={handleResizeStart('right')} />
        </>
      )}

      <ContextMenu
        isVisible={contextMenu.isVisible}
        x={contextMenu.x}
        y={contextMenu.y}
        items={contextMenu.items}
        onClose={hideContextMenu}
      />

    </div>
  );
};